import catalogJson from "./C1_EloItemCatalog.json";
import type { C1_EloItem_Definition } from "../types/C1_EloTypes";
import { C1_EloItem_Type } from "../types/C1_EloTypes";
import {
  getButtonProductDefinition,
  getLightProductDefinition,
  type C1_LightProductDefinition,
} from "./C1_ProductDefinitions";

export type C1_EloItemCatalogEntry = Partial<Omit<C1_EloItem_Definition, "id" | "items" | "type">> & {
  /** Optional base item resolved before the local definition overrides it. */
  extends?: string;
  type?: Exclude<C1_EloItem_Definition["type"], C1_EloItem_Type.Group>;
  items?: string[];
};

export type C1_EloItemCatalogData = {
  version: number;
  /** Display-only dictionary. Never copied into device names or export data. */
  paletteHyphenation?: Record<string, Record<string, string>>;
  lightUsageCategories: Record<string, { description: { de: string; en: string } }>;
  lightTypes: Record<string, C1_LightProductDefinition>;
  items: Record<string, C1_EloItemCatalogEntry>;
  /** A direct item reference places that ELO item itself; a list equips a furniture/room owner. */
  profiles: Record<string, string | string[]>;
  roomTypeProfiles: Record<string, string>;
};

export function getCatalogProfileItemIds(profile: string | string[] | undefined): string[] {
  return typeof profile === "string" ? [profile] : profile ?? [];
}

/** The catalog structure determines whether placement needs a separate furniture owner. */
export function isStandaloneEloProfile(profileId: string, data: C1_EloItemCatalogData = catalog): boolean {
  return typeof data.profiles[profileId] === "string";
}

// JSON imports widen enum literals to string. Runtime validation below protects
// the external-data boundary before the catalog is exposed to the application.
const catalogErrors: string[] = [];
const reportedCatalogErrors = new Set<string>();
const validItemTypes = new Set<string>(
  Object.values(C1_EloItem_Type).filter((type) => type !== C1_EloItem_Type.Group),
);
const rawCatalog = catalogJson as unknown;

function isCatalogRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** Deeply merges objects; arrays and scalar values replace inherited values. */
function mergeCatalogValues(baseValue: unknown, localValue: unknown): unknown {
  if (!isCatalogRecord(baseValue) || !isCatalogRecord(localValue)) return localValue;

  const result: Record<string, unknown> = { ...baseValue };
  for (const key of Object.keys(localValue)) {
    result[key] = mergeCatalogValues(baseValue[key], localValue[key]);
  }
  return result;
}

/** Resolves one raw catalog item including its complete `extends` chain. */
export function resolveCatalogEntry(
  data: C1_EloItemCatalogData,
  id: string,
  resolving: Set<string> = new Set<string>(),
): C1_EloItemCatalogEntry | undefined {
  return resolveCatalogEntryFromItems(data.items, id, resolving);
}

/** Resolves inheritance when only the catalog's item dictionary is available. */
export function resolveCatalogEntryFromItems(
  items: Record<string, C1_EloItemCatalogEntry>,
  id: string,
  resolving: Set<string> = new Set<string>(),
): C1_EloItemCatalogEntry | undefined {
  const source = items[id];
  if (!source || resolving.has(id)) return undefined;
  if (!source.extends) return { ...source };

  const nextResolving = new Set(resolving);
  nextResolving.add(id);
  const inherited = resolveCatalogEntryFromItems(items, source.extends, nextResolving);
  if (!inherited) return undefined;

  const merged = mergeCatalogValues(inherited, source) as C1_EloItemCatalogEntry;
  delete merged.extends;
  return merged;
}

function reportCatalogError(message: string) {
  if (reportedCatalogErrors.has(message)) return;
  reportedCatalogErrors.add(message);
  catalogErrors.push(message);
  console.error(`[C1 Elo catalog] ${message}`);
}

const catalog: C1_EloItemCatalogData =
  rawCatalog && typeof rawCatalog === "object" && !Array.isArray(rawCatalog)
    ? (rawCatalog as C1_EloItemCatalogData)
    : (() => {
        reportCatalogError("Die Katalogdatei enthält kein gültiges JSON-Objekt.");
        return { version: 2, lightUsageCategories: {}, lightTypes: {}, items: {}, profiles: {}, roomTypeProfiles: {} };
      })();

function validateCatalog(data: C1_EloItemCatalogData) {
  if (!Number.isInteger(data.version) || data.version < 1) {
    reportCatalogError("Der Katalog besitzt keine gültige Versionsnummer.");
  }
  if (!data.items || typeof data.items !== "object" || Array.isArray(data.items)) {
    reportCatalogError('Der Katalog benötigt ein Objekt "items".');
    data.items = {};
  }
  if (!data.profiles || typeof data.profiles !== "object" || Array.isArray(data.profiles)) {
    reportCatalogError('Der Katalog benötigt ein Objekt "profiles".');
    data.profiles = {};
  }
  if (!data.roomTypeProfiles || typeof data.roomTypeProfiles !== "object" || Array.isArray(data.roomTypeProfiles)) {
    reportCatalogError('Der Katalog benötigt ein Objekt "roomTypeProfiles".');
    data.roomTypeProfiles = {};
  }
  if (!data.lightUsageCategories || typeof data.lightUsageCategories !== "object" || Array.isArray(data.lightUsageCategories)) {
    reportCatalogError('Der Katalog benötigt ein Objekt "lightUsageCategories".');
    data.lightUsageCategories = {};
  }
  if (!data.lightTypes || typeof data.lightTypes !== "object" || Array.isArray(data.lightTypes)) {
    reportCatalogError('Der Katalog benötigt ein Objekt "lightTypes".');
    data.lightTypes = {};
  }

  for (const [id, lightType] of Object.entries(data.lightTypes)) {
    if (!lightType.descriptionShort?.de || !lightType.descriptionShort?.en || !lightType.description?.de || !lightType.description?.en) {
      reportCatalogError(`Leuchtenbauart "${id}" besitzt keine vollständigen Beschreibungen.`);
    }
    if (!lightType.icon?.name) reportCatalogError(`Leuchtenbauart "${id}" besitzt kein Icon.`);
    if (!Array.isArray(lightType.usage) || lightType.usage.length === 0) {
      reportCatalogError(`Leuchtenbauart "${id}" besitzt keine usage-Kategorien.`);
    } else {
      for (const usage of lightType.usage) {
        if (!Object.prototype.hasOwnProperty.call(data.lightUsageCategories, usage)) {
          reportCatalogError(`Leuchtenbauart "${id}" referenziert die unbekannte usage-Kategorie "${usage}".`);
        }
      }
    }
  }

  for (const profileId of Object.keys(data.profiles)) {
    const profile = data.profiles[profileId];
    if (typeof profile !== "string" && !Array.isArray(profile)) {
      reportCatalogError(`Profil "${profileId}" benötigt einen Item-Verweis oder eine Item-Liste.`);
      data.profiles[profileId] = [];
    }
    if (data.items[profileId]) {
      reportCatalogError(`Die ID "${profileId}" wird gleichzeitig als Item und Profil verwendet.`);
    }
  }

  for (const [roomType, profileId] of Object.entries(data.roomTypeProfiles)) {
    if (!Object.prototype.hasOwnProperty.call(data.profiles, profileId)) {
      reportCatalogError(`Raumtyp "${roomType}" referenziert das unbekannte Profil "${profileId}".`);
    }
  }

  for (const [id, rawItem] of Object.entries(data.items)) {
    if (!rawItem || typeof rawItem !== "object" || Array.isArray(rawItem)) {
      reportCatalogError(`Item "${id}" ist kein gültiges Objekt und wird ignoriert.`);
      delete data.items[id];
      continue;
    }
    if (rawItem.extends && !data.items[rawItem.extends]) {
      reportCatalogError(`Item "${id}" erweitert das unbekannte Item "${rawItem.extends}".`);
      continue;
    }
    if (rawItem.extends && !resolveCatalogEntry(data, id)) {
      reportCatalogError(`Zirkuläre extends-Referenz bei Item "${id}".`);
      continue;
    }
    const item = resolveCatalogEntry(data, id) ?? rawItem;
    const isGroup = Array.isArray(item.items);
    const isPlugGroup = isGroup && item.type === C1_EloItem_Type.PlugGroup;
    if (isGroup && item.type !== undefined && !isPlugGroup) {
      reportCatalogError(`Gruppe "${id}" enthält zusätzlich einen type; der type wird ignoriert.`);
    }
    if (!isGroup && item.type === undefined) {
      reportCatalogError(`Item "${id}" besitzt weder einen type noch ein items-Array und wird ignoriert.`);
    } else if (!isGroup && !validItemTypes.has(item.type as string)) {
      reportCatalogError(`Item "${id}" besitzt den unbekannten type "${String(item.type)}" und wird ignoriert.`);
    }
    if (!item.description?.de || !item.description?.en) {
      reportCatalogError(`Item "${id}" besitzt keine vollständige deutsche und englische Beschreibung.`);
    }
    if (item.descriptionShort && (!item.descriptionShort.de || !item.descriptionShort.en)) {
      reportCatalogError(`Item "${id}" besitzt keine vollständige descriptionShort.`);
    }
    if (item.displayPosition && (!item.displayPosition.de || !item.displayPosition.en)) {
      reportCatalogError(`Item "${id}" besitzt keine vollständige displayPosition.`);
    }
    if (item.allways !== undefined && typeof item.allways !== "boolean") {
      reportCatalogError(`Item "${id}": allways muss boolean sein.`);
    }
    if (item.onlyIfWallDocked !== undefined && typeof item.onlyIfWallDocked !== "boolean") {
      reportCatalogError(`Item "${id}": onlyIfWallDocked muss boolean sein.`);
    }
    if (item.type === C1_EloItem_Type.Button) {
      for (const productId of item.details?.buttonType?.options ?? []) {
        if (!getButtonProductDefinition(productId)) {
          reportCatalogError(`Taster "${id}" referenziert das unbekannte Produkt "${productId}".`);
        }
      }
    }
    if (item.type === C1_EloItem_Type.Light) {
      if (!item.usageCategory || !Object.prototype.hasOwnProperty.call(data.lightUsageCategories, item.usageCategory)) {
        reportCatalogError(`Leuchte "${id}" referenziert keine gültige usageCategory.`);
      }
      const productIds = [
        ...(item.details?.lightProductId?.options ?? []),
        item.defaultDetails?.lightProductId,
      ].filter((productId): productId is NonNullable<typeof productId> => Boolean(productId));
      for (const productId of productIds) {
        if (!getLightProductDefinition(productId)) {
          reportCatalogError(`Leuchte "${id}" referenziert die unbekannte Bauform "${productId}".`);
        } else if (item.usageCategory && !getLightProductDefinition(productId)?.usage.includes(item.usageCategory)) {
          reportCatalogError(`Leuchte "${id}": Bauform "${productId}" ist für "${item.usageCategory}" nicht erlaubt.`);
        }
      }
    }
  }
}

validateCatalog(catalog);

const resolvedItems = new Map<string, C1_EloItem_Definition>();
const resolvedProfiles = new Map<string, C1_EloItem_Definition>();

export function resolveEloItemDefinition(
  id: string,
  resolving = new Set<string>(),
): C1_EloItem_Definition | undefined {
  const cached = resolvedItems.get(id);
  if (cached) return cached;

  const rawSource = catalog.items[id];
  if (!rawSource) {
    reportCatalogError(`Unbekannte Item-Referenz "${id}"; die Referenz wird ausgelassen.`);
    return undefined;
  }
  if (resolving.has(id)) {
    reportCatalogError(`Zirkuläre Item-Referenz: ${[...resolving, id].join(" → ")}; die Referenz wird ausgelassen.`);
    return undefined;
  }

  const source = resolveCatalogEntry(catalog, id);
  if (!source) {
    reportCatalogError(`Die extends-Kette von "${id}" ist ungÃ¼ltig.`);
    return undefined;
  }

  const isGroup = Array.isArray(source.items);
  if (!isGroup && (source.type === undefined || !validItemTypes.has(source.type as string))) {
    return undefined;
  }
  const nextResolving = new Set(resolving).add(id);
  const definition: C1_EloItem_Definition = {
    ...source,
    id,
    type: isGroup && source.type !== C1_EloItem_Type.PlugGroup
      ? C1_EloItem_Type.Group
      : source.type!,
    hasTag: source.hasTag ?? true,
    description: {
      de: source.description?.de || id,
      en: source.description?.en || id,
    },
    items: source.items?.flatMap((childId) => {
      const child = resolveEloItemDefinition(childId, nextResolving);
      return child ? [child] : [];
    }),
  };
  resolvedItems.set(id, definition);
  return definition;
}

export function resolveEloItemProfile(profileId: string): C1_EloItem_Definition[] {
  return resolveEloItemProfileDefinition(profileId).items ?? [];
}

export function resolveEloItemProfileDefinition(profileId: string): C1_EloItem_Definition {
  const cached = resolvedProfiles.get(profileId);
  if (cached) return cached;

  const profile = catalog.profiles[profileId];
  if (typeof profile !== "string" && !Array.isArray(profile)) {
    reportCatalogError(`Unbekanntes oder ungültiges Elo-Profil "${profileId}".`);
    return {
      id: profileId,
      type: C1_EloItem_Type.Group,
      hasTag: false,
      defaultEnabled: true,
      description: { de: profileId, en: profileId },
      includeInDescription: false,
      items: [],
    };
  }

  const itemIds = getCatalogProfileItemIds(profile);

  const definition: C1_EloItem_Definition = {
    id: profileId,
    type: C1_EloItem_Type.Group,
    hasTag: false,
    defaultEnabled: true,
    description: { de: profileId, en: profileId },
    includeInDescription: false,
    items: itemIds.flatMap((id) => {
      const item = resolveEloItemDefinition(id);
      return item ? [item] : [];
    }),
  };
  resolvedProfiles.set(profileId, definition);
  return definition;
}

export function hasEloItemProfile(profileId: string): boolean {
  return Object.prototype.hasOwnProperty.call(catalog.profiles, profileId);
}

export function getEloItemCatalog(): Readonly<C1_EloItemCatalogData> {
  return catalog;
}

export function getEloItemCatalogErrors(): readonly string[] {
  return catalogErrors;
}

// Resolve once during module initialization. Invalid references are collected,
// while all valid catalog branches remain available to the application.
for (const profileId of Object.keys(catalog.profiles)) {
  resolveEloItemProfileDefinition(profileId);
}
